import { Box, Button, CircularProgress } from '@material-ui/core';
import CustomDatepicker from 'components/Form/Input/CustomDatepicker';
import React, { Component } from 'react';
import { Field, Form } from 'react-final-form';
import { WithTranslation, withTranslation } from 'react-i18next';
import { required, requiredAutocomplete } from 'utils/customValidators';
import { IWithAddContent, withAddContent } from '../HOC/WithAddContent';
import addContentParser from '../HOC/WithAddContent.parser';
import CustomField from '../Input/CustomField';
import CustomSelect from '../Input/CustomSelect/CustomSelect';
import CustomTextArea from '../Input/CustomTextArea';
import DraggableSelect from '../Input/DraggableSelect/DraggableSelect';
import {
  AddContentFormButtonContainer,
  AddContentFormColumn,
  AddContentFormContainer,
  AddContentFormDiv,
  AddContentFormRow,
} from './AddManyContentsForm.styled';
import AddManyContentsTable from './AddManyContentsTable/AddManyContentsTable';

interface IState {
  isProjectNameDisabled: boolean;
}

type PropType = WithTranslation & IWithAddContent;

export class AddManyContentsForm extends Component<PropType, IState> {
  private parser: typeof addContentParser;

  constructor(props: PropType) {
    super(props);
    this.parser = addContentParser;

    this.state = {
      isProjectNameDisabled: false,
    };
  }

  componentDidMount(): void {
    const { getCustomers, getProjectPrefix, getAvailableLanguages, form } = this.props;

    form.subscribe(
      ({ values: { activeProject } }) => {
        if (!!activeProject) {
          this.setState({ isProjectNameDisabled: true });
        } else {
          this.setState({ isProjectNameDisabled: false });
        }
      },
      { values: true },
    );

    getCustomers();
    getAvailableLanguages();
    getProjectPrefix();
  }

  render(): JSX.Element {
    const {
      t,
      xtmCustomer,
      xtmTemplate,
      availableLanguages,
      activeProjects,
      xtmCustomerSpinner,
      xtmTemplateSpinner,
      activeProjectsSpinner,
      languagesSpinner,
      translateSpinner,
      contentSpinner,
      defaultSourceLanguage,
      defaultCustomer,
      defaultTemplate,
      handleCustomerChange,
      handleSourceLanguageChange,
      handleTargetLanguagesChange,
      handleSubmitForm,
      contentState: { selected },
      targetLanguages,
      form,
      defaultDueDate,
    } = this.props;

    const parsed = {
      customers: this.parser.parseCustomers(xtmCustomer),
      templates: this.parser.parseTemplates(xtmTemplate),
      activeProjects: this.parser.parseProjects(activeProjects),
    };

    const { isProjectNameDisabled } = this.state;

    return (
      <AddContentFormDiv>
        <AddContentFormRow>
          <AddContentFormColumn>
            <Form
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onSubmit={(): void => {}}
              subscription={{ pristine: true, submitting: true }}
              form={form}
              render={({ handleSubmit }): JSX.Element => (
                <AddContentFormContainer onSubmit={handleSubmit}>
                  <Field
                    name="xtmCustomer"
                    validate={requiredAutocomplete}
                    component={CustomSelect}
                    label="addcontent.xtmCustomer"
                    options={parsed.customers}
                    onChange={handleCustomerChange}
                    spinner={xtmCustomerSpinner}
                    initialValue={defaultCustomer}
                    placeholder={t('common.selectPlaceholder')}
                    disableClearable
                  />
                  <Field
                    name="xtmTemplate"
                    component={CustomSelect}
                    validate={requiredAutocomplete}
                    label="addcontent.xtmTemplate"
                    options={parsed.templates}
                    spinner={xtmTemplateSpinner}
                    initialValue={defaultTemplate}
                    placeholder={t('common.selectPlaceholder')}
                    disableClearable
                  />
                  <Field
                    name="sourceLanguage"
                    validate={requiredAutocomplete}
                    component={CustomSelect}
                    disabled={contentSpinner}
                    label="addcontent.sourceLanguage"
                    options={this.parser.parseLanguages(availableLanguages)}
                    onChange={handleSourceLanguageChange(true)}
                    spinner={languagesSpinner}
                    initialValue={defaultSourceLanguage}
                    placeholder={t('common.selectPlaceholder')}
                    disableClearable
                  />
                  <Field
                    name="targetLanguages"
                    component={DraggableSelect}
                    validate={requiredAutocomplete}
                    label="addcontent.targetLanguages"
                    options={this.parser.parseLanguages(targetLanguages)}
                    onChange={handleTargetLanguagesChange}
                    spinner={languagesSpinner}
                    placeholder={t('common.selectPlaceholder')}
                  />
                  <Field
                    name="projectName"
                    component={CustomField}
                    validate={isProjectNameDisabled ? undefined : required}
                    key={isProjectNameDisabled ? 'projectNameNoValidate' : 'projectName'}
                    disabled={isProjectNameDisabled}
                    label="addcontent.projectName"
                  />
                  <Field
                    name="description"
                    component={CustomTextArea}
                    label="addcontent.description"
                    rowsMin={2}
                    disabled={isProjectNameDisabled}
                  />
                  <Field
                    name="activeProject"
                    component={CustomSelect}
                    label="addcontent.activeXtmProjects"
                    options={parsed.activeProjects}
                    spinner={activeProjectsSpinner}
                    placeholder={t('common.selectPlaceholder')}
                  />
                  <Field
                    name="dueDate"
                    component={CustomDatepicker}
                    initialValue={defaultDueDate}
                    label="addcontent.dueDate"
                  />
                  <AddContentFormButtonContainer>
                    <Button
                      size="medium"
                      variant="outlined"
                      color="primary"
                      onClick={handleSubmitForm(true)}
                      disabled={!Boolean(selected?.length) || translateSpinner}
                    >
                      {translateSpinner ? (
                        <CircularProgress size="24px" color="secondary" />
                      ) : (
                        <Box fontWeight="500" fontSize="13px">
                          {t('addcontent.addToQueue')}
                        </Box>
                      )}
                    </Button>
                    <Button
                      size="medium"
                      variant="contained"
                      color="secondary"
                      onClick={handleSubmitForm(false)}
                      disabled={!Boolean(selected?.length) || translateSpinner}
                    >
                      {translateSpinner ? (
                        <CircularProgress size="24px" color="secondary" />
                      ) : (
                        <Box fontWeight="500" fontSize="13px">
                          {t('addcontent.sendForTranslation')}
                        </Box>
                      )}
                    </Button>
                  </AddContentFormButtonContainer>
                </AddContentFormContainer>
              )}
            />
          </AddContentFormColumn>
          <AddContentFormColumn>
            <AddManyContentsTable />
          </AddContentFormColumn>
        </AddContentFormRow>
      </AddContentFormDiv>
    );
  }
}

export default withAddContent(withTranslation()(AddManyContentsForm));
